import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Alert, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  CircularProgress
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { ExcelRenderer } from 'react-excel-renderer';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SCurveImport = ({ onImport }) => {
  const [uploadedData, setUploadedData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Check file type
    const fileType = file.name.split('.').pop().toLowerCase();
    if (fileType !== 'xlsx' && fileType !== 'xls') {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    ExcelRenderer(file, (err, resp) => {
      setLoading(false);
      
      if (err) {
        console.error('Error parsing Excel file:', err);
        setError('Failed to parse Excel file. Please check the format and try again.');
        return;
      }
      
      try {
        // Extract headers and rows
        const headers = resp.rows[0];
        const rows = resp.rows.slice(1);
        
        // Validate required columns
        const requiredColumns = ['Date', 'Planned Progress (%)', 'Actual Progress (%)'];
        const headerMap = {};
        
        // Find column indices
        headers.forEach((header, index) => {
          requiredColumns.forEach(reqCol => {
            if (header.includes(reqCol) || header.includes(reqCol.toLowerCase())) {
              headerMap[reqCol] = index;
            }
          });
        });
        
        // Check if all required columns are found
        const missingColumns = requiredColumns.filter(col => headerMap[col] === undefined);
        
        if (missingColumns.length > 0) {
          setError(`Missing required columns: ${missingColumns.join(', ')}. Please download the template and try again.`);
          return;
        }
        
        // Transform data to our format
        const transformedData = rows
          .filter(row => row.length > 0 && row[headerMap['Date']]) // Filter out empty rows
          .map(row => {
            let date;
            
            // Parse date
            if (typeof row[headerMap['Date']] === 'number') {
              // Convert Excel date number to JS date
              date = new Date(Math.round((row[headerMap['Date']] - 25569) * 86400 * 1000));
            } else {
              // Try to parse string date
              date = new Date(row[headerMap['Date']]);
            }
            
            // Format date
            const formattedDate = date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
            
            return {
              date: formattedDate,
              planned: parseFloat(row[headerMap['Planned Progress (%)']] || 0),
              actual: parseFloat(row[headerMap['Actual Progress (%)']] || 0)
            };
          });
        
        // Validate data
        const invalidRows = transformedData.filter(
          row => isNaN(row.planned) || isNaN(row.actual) || isNaN(new Date(row.date).getTime())
        );
        
        if (invalidRows.length > 0) {
          setError(`Found ${invalidRows.length} invalid rows. Please check that all values are numeric and dates are valid.`);
          return;
        }
        
        setUploadedData(transformedData);
      } catch (error) {
        console.error('Error processing Excel data:', error);
        setError('Failed to process Excel data. Please check the format and try again.');
      }
    });
  };
  
  // Download template
  const downloadTemplate = () => {
    // Create template data
    const templateData = [
      [
        'Date', 
        'Planned Progress (%)', 
        'Actual Progress (%)', 
        'Notes'
      ],
      [
        new Date(2025, 4, 15), 
        10, 
        8, 
        'Week 1'
      ],
      [
        new Date(2025, 4, 22), 
        20, 
        18, 
        'Week 2'
      ],
      [
        new Date(2025, 4, 29), 
        35, 
        30, 
        'Week 3'
      ],
      [
        new Date(2025, 5, 5), 
        50, 
        45, 
        'Week 4'
      ],
      [
        new Date(2025, 5, 12), 
        65, 
        60, 
        'Week 5'
      ],
      [
        new Date(2025, 5, 19), 
        80, 
        75, 
        'Week 6'
      ],
      [
        new Date(2025, 5, 26), 
        100, 
        95, 
        'Week 7'
      ]
    ];
    
    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    
    // Set column widths
    const wscols = [
      { wch: 15 },  // Date
      { wch: 20 },  // Planned Progress
      { wch: 20 },  // Actual Progress
      { wch: 30 }   // Notes
    ];
    worksheet['!cols'] = wscols;
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'S-Curve Template');
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Save file
    saveAs(blob, 'scurve_template.xlsx');
  };
  
  // Handle import
  const handleImport = () => {
    if (uploadedData && uploadedData.length > 0) {
      onImport(uploadedData);
    }
  };
  
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#5D4037' }}>
        Import S-Curve Data from Excel
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Upload an Excel file with your S-Curve data. The file must include columns for Date, Planned Progress (%), and Actual Progress (%).
        You can download a template below to get started.
      </Alert>
      
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={downloadTemplate}
          sx={{ 
            color: '#5D4037', 
            borderColor: '#5D4037',
            '&:hover': { 
              backgroundColor: '#F9F5EB',
              borderColor: '#8D6E63'
            }
          }}
        >
          Download Template
        </Button>
        
        <Button
          variant="contained"
          component="label"
          startIcon={<FileUploadIcon />}
          sx={{ 
            bgcolor: '#F0C14B', 
            '&:hover': { bgcolor: '#DDA726' },
            color: '#5D4037',
            fontWeight: 'bold'
          }}
        >
          Upload Excel File
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleFileUpload}
          />
        </Button>
      </Box>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#F0C14B' }} />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {uploadedData && uploadedData.length > 0 && (
        <>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ mr: 1 }} />
              <Typography>
                Successfully parsed {uploadedData.length} data points from the Excel file. Review the data below and click "Import Data" to generate the S-Curve.
              </Typography>
            </Box>
          </Alert>
          
          <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9F5EB' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Planned Progress (%)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actual Progress (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadedData.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{item.date}</TableCell>
                    <TableCell>{item.planned}%</TableCell>
                    <TableCell>{item.actual}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => setUploadedData(null)}
              sx={{ 
                color: '#5D4037', 
                borderColor: '#5D4037',
                '&:hover': { 
                  backgroundColor: '#F9F5EB',
                  borderColor: '#8D6E63'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleImport}
              sx={{ 
                bgcolor: '#F0C14B', 
                '&:hover': { bgcolor: '#DDA726' },
                color: '#5D4037',
                fontWeight: 'bold'
              }}
            >
              Import Data
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SCurveImport;
